<template>
  <div class="subscription-page">
    <div v-if="isLoading">
      <CanPayLoader />
    </div>
    <div class="container pt-3 pb-4" v-if="subscription">
      <div class="subscription-card mb-3">
        <div class="card-top-row">
          <div class="subscription-title">{{ subscription.membership_program_title || 'Membership Program' }}</div>
          <div class="amount">${{ formatMoney(subscription.amount) }}</div>
        </div>
        <div class="subscription-reference-box" v-if="subscription.subscription_reference">
          <div class="subscription-reference-label">Subscription Reference</div>
          <div class="subscription-reference">{{ subscription.subscription_reference }}</div>
        </div>
        <div class="subscription-description" v-if="subscription.membership_description">{{ subscription.membership_description }}</div>
        <div class="period-chip">{{ formatPeriod(subscription.membership_period) }}</div>
        <div class="meta-separator"></div>
        <div class="meta-row">
          <span class="meta-label">Status</span>
          <span class="meta-value" :class="{ 'status-pill': isSubscriptionActive, 'status-pill-inactive': !isSubscriptionActive }">{{ getSubscriptionStatusLabel(subscription) }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">Next Billing</span>
          <span class="meta-value">{{ formatDate(subscription.next_billing_date) }}</span>
        </div>
        <div class="meta-row" v-if="showRetryAttempts">
          <span class="meta-label">Retry Attempts</span>
          <span class="meta-value">{{ subscription.retry_attempt_count || 0 }}/3</span>
        </div>
        <div class="meta-row" v-if="showRetryAt">
          <span class="meta-label">Next Auto Retry</span>
          <span class="meta-value">{{ formatDate(subscription.retry_at) }}</span>
        </div>
        <div class="action-row">
          <label class="points-toggle-card" :class="{ 'points-toggle-disabled': !isSubscriptionActive }">
            <input type="checkbox" :checked="Number(subscription.use_available_points) === 1" :disabled="!isSubscriptionActive" @change="requestTogglePointsPreference($event)" />
            <span class="points-toggle-copy">
              <span class="points-toggle-title">Use available points</span>
              <span class="points-toggle-subtitle">Apply points first, then charge bank for the remaining amount.</span>
            </span>
          </label>
          <button
            v-if="isSubscriptionActive"
            class="cancel-btn"
            @click="requestCancelSubscription"
          >
            Cancel Subscription
          </button>
        </div>
        <div class="action-note" v-if="!isSubscriptionActive">
          This subscription is not active. Points preference cannot be changed.
        </div>
        <div class="lifecycle-note lifecycle-note-error" v-if="isInitialFailureTerminated">
          Initial subscription payment failed. This subscription is closed and cannot be retried. Please create a new subscription.
        </div>
        <div class="lifecycle-note lifecycle-note-warning" v-else-if="isRecurringRetryInProgress">
          Recurring billing failed. CanPay will retry this subscription automatically on the next retry date.
        </div>
        <div class="lifecycle-note lifecycle-note-error" v-else-if="isRecurringRetryExhausted">
          Recurring billing failed 3 times. Auto-retry is stopped. Please retry from the app.
        </div>
        <div class="lifecycle-note lifecycle-note-muted" v-else-if="isNonRetryableClosed">
          This subscription is closed (cancelled or returned) and will not be retried.
        </div>
      </div>

      <div class="subscription-card" v-for="item in history" :key="item.id">
        <div class="history-top-row">
          <div class="attempt-title">Cycle {{ item.billing_cycle_number }} - {{ formatPeriod(item.billing_type) }}</div>
          <div class="history-status-pill">{{ item.status }}</div>
        </div>
        <div class="meta-separator"></div>
        <div class="meta-row">
          <span class="meta-label">Points Used</span>
          <span class="meta-value">${{ formatMoney(item.points_applied_amount) }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">Bank Charged</span>
          <span class="meta-value">${{ formatMoney(item.bank_charged_amount) }}</span>
        </div>
        <div class="meta-row">
          <span class="meta-label">Attempted At</span>
          <span class="meta-value">{{ formatDate(item.attempted_at) }}</span>
        </div>
        <div class="meta-message" v-if="item.response_message">{{ item.response_message }}</div>
      </div>

      <div v-if="confirmModal.show" class="confirm-modal-overlay" @click="closeConfirmModal">
        <div class="confirm-modal-card" @click.stop>
          <div class="confirm-modal-title">{{ confirmModal.title }}</div>
          <div class="confirm-modal-message">{{ confirmModal.message }}</div>
          <div class="confirm-modal-actions">
            <button class="confirm-secondary" @click="closeConfirmModal">No</button>
            <button class="confirm-primary" @click="confirmAction">Yes</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import subscriptionApi from "../../api/subscription";
import CanPayLoader from "../CustomLoader/CanPayLoader.vue";
import moment from "moment";

export default {
  name: "SubscriptionDetails",
  components: { CanPayLoader },
  computed: {
    isSubscriptionActive() {
      if (!this.subscription) {
        return false;
      }
      if (Number(this.subscription.status_code) === 204) {
        return true;
      }
      return String(this.subscription.status || "").toLowerCase() === "active";
    },
    showRetryAttempts() {
      if (!this.subscription) return false;
      return Number(this.subscription.retry_attempt_count || 0) > 0;
    },
    showRetryAt() {
      if (!this.subscription) return false;
      return !!this.subscription.retry_at && this.isRecurringRetryInProgress;
    },
    isInitialFailureTerminated() {
      if (!this.subscription) return false;
      return Number(this.subscription.is_initial_failed || 0) === 1;
    },
    isRecurringRetryInProgress() {
      if (!this.subscription) return false;
      const failed = String(this.getSubscriptionStatusLabel(this.subscription)).toLowerCase() === "failed";
      return failed && Number(this.subscription.is_initial_failed || 0) === 0 && Number(this.subscription.is_auto_renew || 0) === 1 && Number(this.subscription.retry_attempt_count || 0) > 0 && Number(this.subscription.retry_attempt_count || 0) < 3;
    },
    isRecurringRetryExhausted() {
      if (!this.subscription) return false;
      const failed = String(this.getSubscriptionStatusLabel(this.subscription)).toLowerCase() === "failed";
      return failed && Number(this.subscription.is_initial_failed || 0) === 0 && Number(this.subscription.is_auto_renew || 0) === 0 && Number(this.subscription.retry_attempt_count || 0) >= 3;
    },
    isNonRetryableClosed() {
      if (!this.subscription) return false;
      const statusText = String(this.subscription.status || "").toLowerCase();
      return statusText.includes("void") || statusText.includes("return") || statusText.includes("declined");
    },
  },
  data() {
    return {
      isLoading: false,
      subscription: null,
      history: [],
      confirmModal: {
        show: false,
        type: null,
        nextValue: null,
        title: "",
        message: "",
      },
    };
  },
  methods: {
    loadData() {
      this.isLoading = true;
      subscriptionApi
        .subscriptionHistory({ subscription_reference: this.$route.params.reference })
        .then((response) => {
          this.isLoading = false;
          this.subscription = response.data ? response.data.subscription : null;
          this.history = response.data ? response.data.history : [];
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    formatDate(value) {
      return value ? moment(value).format("MMM DD, YYYY hh:mm A") : "-";
    },
    formatMoney(value) {
      const amount = value === null || value === undefined ? 0 : value;
      return parseFloat(amount).toFixed(2);
    },
    formatPeriod(value) {
      if (!value) {
        return "-";
      }
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    requestTogglePointsPreference(event) {
      if (!this.isSubscriptionActive) {
        event.target.checked = Number(this.subscription.use_available_points) === 1;
        return;
      }
      const enabled = event.target.checked;
      event.target.checked = Number(this.subscription.use_available_points) === 1;
      this.confirmModal = {
        show: true,
        type: "toggle_points",
        nextValue: enabled,
        title: "Confirm Change",
        message: `Are you sure you want to ${enabled ? "enable" : "disable"} available points for this subscription?`,
      };
    },
    applyTogglePointsPreference(enabled) {
      subscriptionApi
        .updatePointsPreference({
          subscription_reference: this.$route.params.reference,
          use_available_points: enabled,
        })
        .then(() => {
          this.subscription.use_available_points = enabled ? 1 : 0;
        })
        .catch(() => {
          alert("Unable to update points preference. Please try again.");
        });
    },
    requestCancelSubscription() {
      this.confirmModal = {
        show: true,
        type: "cancel_subscription",
        nextValue: null,
        title: "Cancel Subscription",
        message: "Are you sure you want to cancel this subscription?",
      };
    },
    applyCancelSubscription() {
      subscriptionApi
        .cancelSubscription({
          subscription_reference: this.$route.params.reference,
        })
        .then(() => {
          this.subscription.status = "Consumer Declined";
          this.subscription.status_code = 1203;
          this.subscription.failed_reason = "Consumer Declined";
        })
        .catch(() => {
          alert("Unable to cancel subscription. Please try again.");
        });
    },
    getSubscriptionStatusLabel(subscription) {
      if (!subscription) {
        return "-";
      }

      if (Number(subscription.status_code) === 204 || String(subscription.status || "").toLowerCase() === "active") {
        return "Active";
      }

      const reason = String(subscription.failed_reason || "").toLowerCase();
      const statusText = String(subscription.status || "").toLowerCase();
      const statusCode = Number(subscription.status_code);

      if (reason.includes("consumer declined") || reason.includes("declined by consumer") || reason.includes("cancelled by consumer") || reason.includes("canceled by consumer")) {
        return "Consumer Declined";
      }

      if (statusCode === 1203 || statusCode === 205 || statusText.includes("declined") || statusText.includes("failed") || reason.length > 0) {
        return "Failed";
      }

      return subscription.status || "Failed";
    },
    closeConfirmModal() {
      this.confirmModal.show = false;
    },
    confirmAction() {
      const modal = this.confirmModal;
      this.closeConfirmModal();
      if (modal.type === "toggle_points") {
        this.applyTogglePointsPreference(modal.nextValue);
      } else if (modal.type === "cancel_subscription") {
        this.applyCancelSubscription();
      }
    },
  },
  mounted() {
    this.loadData();
    this.$root.$emit("changeWhiteBackground", [false, false, "SubscriptionDetailHeader"]);
  },
};
</script>

<style scoped>
.subscription-page {
  min-height: 100vh;
  background-color: #149240;
  padding-bottom: 20px;
}

.subscription-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #e4e8e6;
  box-shadow: 0 2px 6px rgba(13, 84, 42, 0.08);
}

.card-top-row,
.history-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.subscription-title {
  font-weight: 700;
  color: #0f5f2c;
  font-size: 20px;
  margin-bottom: 0;
  line-height: 1.2;
}

.subscription-description {
  margin-top: 5px;
  font-size: 12px;
  color: #4f5f58;
  line-height: 1.3;
}

.subscription-reference {
  font-size: 11px;
  font-weight: 700;
  color: #4d5f56;
  word-break: break-word;
}

.subscription-reference-box {
  margin-top: 10px;
  display: inline-flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #f5f8f6;
  border: 1px solid #dfe7e2;
}

.subscription-reference-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #829187;
}

.amount {
  font-size: 20px;
  font-weight: 700;
  color: #1f1f1f;
  margin-bottom: 0;
  white-space: nowrap;
}

.period-chip {
  display: inline-block;
  margin-top: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #21643c;
  background: #edf8f0;
  border: 1px solid #cce6d4;
  border-radius: 999px;
  padding: 2px 8px;
}

.meta-separator {
  border-top: 1px dashed #d5ddd8;
  margin: 8px 0 7px;
}

.attempt-title {
  font-size: 13px;
  font-weight: 700;
  color: #173f26;
  margin-bottom: 0;
}

.history-status-pill {
  font-size: 11px;
  color: #0d5a2b;
  background: #ecf7f0;
  border: 1px solid #cde7d6;
  border-radius: 999px;
  padding: 2px 7px;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.meta-label {
  font-size: 12px;
  color: #6f7471;
}

.meta-value {
  font-size: 12px;
  font-weight: 600;
  color: #232323;
  text-align: right;
}

.status-pill {
  font-size: 11px;
  color: #0d5a2b;
  background: #ecf7f0;
  border: 1px solid #cde7d6;
  border-radius: 999px;
  padding: 2px 7px;
}

.status-pill-inactive {
  font-size: 11px;
  color: #7a2424;
  background: #fff0f0;
  border: 1px solid #efcaca;
  border-radius: 999px;
  padding: 2px 7px;
}

.meta-message {
  margin-top: 7px;
  font-size: 12px;
  color: #6a2e2e;
  text-align: center;
}

.points-toggle-card {
  flex: 1;
  min-width: 220px;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f7faf8;
  border: 1px solid #dde9e2;
  font-size: 12px;
}

.points-toggle-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.points-toggle-title {
  color: #153f28;
  font-weight: 700;
}

.points-toggle-subtitle {
  color: #6d7a73;
  font-size: 11px;
  line-height: 1.25;
}

.points-toggle-disabled {
  opacity: 0.55;
}

.action-note {
  width: 100%;
  margin-top: 6px;
  font-size: 11px;
  color: #7a2424;
}

.lifecycle-note {
  width: 100%;
  margin-top: 8px;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
}

.lifecycle-note-error {
  background: #fff1f0;
  color: #a7342d;
  border: 1px solid #f2c6c2;
}

.lifecycle-note-warning {
  background: #fff8e8;
  color: #8a5c00;
  border: 1px solid #f2dfb5;
}

.lifecycle-note-muted {
  background: #f4f6f5;
  color: #5a655f;
  border: 1px solid #dde3e0;
}

.action-row {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cancel-btn {
  margin-top: 0;
  border: 1px solid #d63f3f;
  color: #c93030;
  background: #fff6f6;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 700;
}

.confirm-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}

.confirm-modal-card {
  width: 100%;
  max-width: 320px;
  background: #fff;
  border-radius: 12px;
  padding: 14px;
}

.confirm-modal-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
}

.confirm-modal-message {
  font-size: 13px;
  color: #414141;
  margin-bottom: 12px;
}

.confirm-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.confirm-secondary,
.confirm-primary {
  border: 0;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
}

.confirm-secondary {
  background: #ececec;
  color: #2f2f2f;
}

.confirm-primary {
  background: #149240;
  color: #fff;
}
</style>
